import React, { useContext, useEffect } from 'react';
import {
  SmallText,
  Header,
  Intro,
  List,
  LegalDisclaimer,
  LegalDisclaimerWrapper,
  PrimaryButton,
  SecondaryButton,
  ButtonContainer,
  Benefit,
  BenefitText,
  HiddenContainer,
  // StyledDivider,
  Lozenges,
} from './components';
import Button from '../../atoms/Button';
import Checkmark from '../../atoms/Checkmark';
import Lozenge from '../../atoms/Lozenge';
import { purify } from '../../utils/helpers';
import { getCookie } from '../../../lib/cookie-utils';

import {
  eventParamAttributes,
  EventParamTypes,
  inViewEventAttributes,
} from '@spotify-internal/touchdown-analytics';
import { white } from '@spotify-internal/encore-web';
import DeviceContext from '../../../configuration/context/device';
import { getDaysRemaining } from '../../../helpers/helpers';
import UserContext from '../../../configuration/context/user';
import { trackEvent } from '../../../lib/tracking';
import {
  componentViewEvent,
  linkClickEvent,
} from '../../../lib/tracking/eventPayloadDefinitions';
import MarketContext from '../../../configuration/context/market';
import { useInView } from 'react-intersection-observer';

type PlanButtonClickEvent = {
  params: EventParamTypes & { position: number };
  browserEvent: React.MouseEvent;
};
type ButtonProps = React.ComponentProps<typeof Button>;
type IncomingButtonProps = ButtonProps & {
  onClick?: (_e: PlanButtonClickEvent) => void;
};

type PlanPropTypes = {
  index: number;
  currentProductText?: string;
  hasDropShadow: boolean;
  length: number;
  plan: {
    offerUuid: string;
    header: string;
    offerDateEnds: number | null;
    offerEnds?: string;
    offerBackground?: string;
    offerTextColor?: string;
    $planColor: string;
    subheader: string;
    button?: IncomingButtonProps;
    secondaryButton?: IncomingButtonProps;
    legalDisclaimer?: string;
    benefits?: { children: string }[];
    accounts?: string;
    isUsersCurrentProduct?: boolean;
    isRecurringProduct?: boolean;
    hasNonRecurringProduct?: boolean;
    lozenges?: React.ComponentProps<typeof Lozenge>[];
    offerTypeId?: string;
    eventParams?: EventParamTypes;
  };
};

const Plan = ({
  plan,
  currentProductText,
  index,
  length,
  hasDropShadow,
}: PlanPropTypes) => {
  const {
    header,
    $planColor,
    subheader,
    offerEnds,
    offerDateEnds,
    offerTextColor,
    offerBackground,
    button,
    secondaryButton,
    legalDisclaimer,
    benefits,
    // accounts,
    isUsersCurrentProduct,
    isRecurringProduct,
    hasNonRecurringProduct,
    lozenges = [],
    offerTypeId,
    eventParams,
  } = plan;
  const { platform } = useContext(DeviceContext);

  const currentPlanButtonHidden =
    isUsersCurrentProduct &&
    currentProductText &&
    isRecurringProduct &&
    !hasNonRecurringProduct;
  const currentPlanBannerVisible = isUsersCurrentProduct && currentProductText;

  const buttonProps: ButtonProps = { ...(button || {}) };
  const secondaryButtonProps: ButtonProps = { ...(secondaryButton || {}) };
  const wrapOnClick =
    (onClick: (_e: PlanButtonClickEvent) => void) =>
    (event: React.MouseEvent) =>
      onClick({
        params: { ...(eventParams || {}), position: index },
        browserEvent: event,
      });
  if (button?.onClick) {
    buttonProps.onClick = wrapOnClick(button.onClick);
  }
  if (secondaryButton?.onClick) {
    secondaryButtonProps.onClick = wrapOnClick(secondaryButton.onClick);
  }
  const user = useContext(UserContext);
  const market = useContext(MarketContext);

  // When button is clicked, trigger a linkClickEvent
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const payload = {
      resource_name: 'premium',
      link_url: e.currentTarget.getAttribute('href') as string,
      country: market?.country ?? 'unknown',
      locale: market?.language ?? 'unknown',
      full_url: window.location.href,
      timestamp: Math.floor(Date.now() / 1000),
      correlation_id: getCookie('sp_t') ?? 'unknown',
      is_evergreen: market?.isEvergreen,
      eligibility: user?.eligibility,
      offer_type_id: offerTypeId,
      component_view: 'storefront',
    };

    trackEvent(
      linkClickEvent(payload, {
        authorize: user.authenticated,
      }),
    );

    e.stopPropagation();
  };

  const daysRemaining = offerDateEnds ? getDaysRemaining(offerDateEnds) : null;

  const [ref, inView] = useInView({
    fallbackInView: true,
    threshold: 0.6,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView === true) {
      const payload = {
        resource_name: 'premium',
        component_name: 'storefront-plan',
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
        offer_type_id: offerTypeId ?? 'unknown',
      };

      trackEvent(
        componentViewEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
  }, [inView, market, user, offerTypeId, eventParams]);

  return (
    <>
      <Intro
        $planColor={$planColor}
        column={index + 1}
        currentPlanVisible={!!currentPlanBannerVisible}
        data-current-plan-text={currentProductText}
        length={length}
        headerLength={header.length}
        hasDropShadow={hasDropShadow}
        {...eventParamAttributes({
          ...eventParams,
          componentName: 'STOREFRONT-PLAN',
          componentPosition: index,
          componentType: 'product',
        })}
        {...inViewEventAttributes()}
        ref={ref}
      >
        {lozenges.length > 0 && (
          <Lozenges $planColor={$planColor}>
            {lozenges.map(
              (lozenge, i) => lozenge && <Lozenge key={i} {...lozenge} />,
            )}
          </Lozenges>
        )}
        <div>
          {header && (
            <Header dangerouslySetInnerHTML={{ __html: purify(header) }} />
          )}
          {subheader && (
            <SmallText
              dangerouslySetInnerHTML={{ __html: purify(subheader) }}
            />
          )}
        </div>
      </Intro>
      <List
        as="ul"
        $planColor={$planColor}
        column={index + 1}
        length={length}
        isLastElement={!legalDisclaimer && !button}
        hasDropShadow={hasDropShadow}
      >
        {!!offerEnds && !!daysRemaining && (
          <Benefit key="benefit-offer">
            <Checkmark strokeWidth="1" aria-hidden="true" />
            <BenefitText
              offerBackground={offerBackground}
              textColor={offerTextColor}
              isTimedOffer={!!offerDateEnds}
              children={purify(
                `${offerEnds.replace('%DAYSREMAINING%', daysRemaining)}`,
              )}
            />
          </Benefit>
        )}
        {benefits &&
          benefits.map(
            (benefit, i) =>
              benefit && (
                <Benefit key={`benefit-${i}`}>
                  <Checkmark
                    strokeWidth="1"
                    $checkmarkColor="#fff"
                    aria-hidden="true"
                  />
                  <BenefitText {...benefit} />
                </Benefit>
              ),
          )}
      </List>
      {button && button.children ? (
        <ButtonContainer
          column={index + 1}
          $planColor={$planColor}
          length={length}
          isLastElement={!legalDisclaimer}
          hasDropShadow={hasDropShadow}
          data-current-plan-button-hidden={currentPlanButtonHidden}
          {...eventParamAttributes({
            ...eventParams,
            componentName: 'STOREFRONT-PLAN',
            componentPosition: index,
            componentType: 'product',
          })}
        >
          {!currentPlanButtonHidden && (
            <>
              <PrimaryButton
                fullWidth
                {...buttonProps}
                $planColor={$planColor}
                onClick={handleButtonClick}
              />
              {secondaryButton && platform !== 'ios' && (
                <SecondaryButton
                  fullWidth
                  $planColor={$planColor}
                  backgroundColor={white}
                  {...secondaryButtonProps}
                  onClick={handleButtonClick}
                />
              )}
            </>
          )}
        </ButtonContainer>
      ) : (
        <HiddenContainer />
      )}
      {legalDisclaimer ? (
        <LegalDisclaimerWrapper
          column={index + 1}
          $planColor={$planColor}
          length={length}
          hasDropShadow={hasDropShadow}
        >
          {!currentPlanButtonHidden && (
            <LegalDisclaimer text={legalDisclaimer} />
          )}
        </LegalDisclaimerWrapper>
      ) : (
        <HiddenContainer />
      )}
    </>
  );
};

export default Plan;
